import React, {
  FunctionComponent,
  useCallback,
  useContext,
  useState,
  useEffect,
} from 'react';
import styled from 'styled-components';
import { Registration } from '@stax-core/types-omni';
import { coreapi } from '../../api';
import {
  AuthStore,
  RegistrationStore,
  updateRegistration,
} from '../../context';
import { useToaster, useWriteableFieldFormValidation } from '../../hooks';
import {
  handleAddressAutocompleteSelect,
  processAddressComponents,
} from '../../util/addressAutocomplete.util';
import { Map } from '../components/Map';
import AddressAutocomplete from '../../components/AddressAutocomplete/AddressAutocomplete';
import { CenteredContent } from '../components/CenteredContent';
import { BackButton } from '../components/BackButton';
import { NextButton } from '../components/NextButton';
import { MainForm } from '../components/application/MainForm';
import { GeocodeResult, getGeocode, getLatLng } from 'use-places-autocomplete';

const AddressField = styled.div`
  margin: 10px 0;
`;

interface FieldValues
  extends Pick<
    Registration,
    | 'business_address_1'
    | 'business_address_city'
    | 'business_address_country'
    | 'business_address_state'
    | 'business_address_zip'
  > {}

export const BusinessAddressPage: FunctionComponent = () => {
  const {
    dispatch,
    state: { registration },
  } = useContext(RegistrationStore);
  const { toaster, toast } = useToaster();
  const {
    state: { auth },
  } = useContext(AuthStore);
  const authToken = auth?.token ?? null;

  // lat: 36 and lng: -96 are coordinates of the United States
  const [latLng, setLatLng] = useState({
    lat: 36,
    lng: -96,
  });
  const [zoom, setZoom] = useState(3);

  //todo render map centered on persisted address on page load
  useEffect(() => {
    const fetchCoords = async () => {
      if (registration?.business_address_1) {
        const address = `${registration.business_address_1}, ${registration.business_address_city}, ${registration.business_address_state}, ${registration.business_address_zip}`;
        const [geoCode] = await getGeocode({ address });
        const { lat, lng } = await getLatLng(geoCode);
        setLatLng({ lat, lng });
        setZoom(15);
      }
    };
    fetchCoords();
  }, [
    registration?.business_address_1,
    registration?.business_address_city,
    registration?.business_address_state,
    registration?.business_address_zip,
  ]);

  const onSelectAddress = async (geoCode: GeocodeResult) => {
    try {
      const { lat, lng } = await getLatLng(geoCode);
      setLatLng({ lat, lng });
      setZoom(15);
    } catch (e) {
      // do nothing
    }
  };

  const onSubmit = useCallback(
    async (formValues: FieldValues) => {
      const payload: Partial<Registration> = {
        business_address_1: formValues.business_address_1 ?? '',
        business_address_city: formValues.business_address_city ?? '',
        business_address_state: formValues.business_address_state ?? '',
        business_address_zip: formValues.business_address_zip ?? '',
        business_address_country: formValues.business_address_country ?? '',
      };

      try {
        const r = await coreapi.put(authToken!, '/team/registration', payload);
        dispatch(updateRegistration(r));
        return true;
      } catch (error) {
        toaster(toast.error(error, 'There was a problem saving your changes.'));
      }
    },
    [authToken, dispatch, toast, toaster],
  );

  const validate = useWriteableFieldFormValidation((values: FieldValues) => {
    const errors: Partial<Record<keyof FieldValues, any>> = {};

    const requiredFields = [
      'business_address_1',
      'business_address_city',
      'business_address_state',
      'business_address_zip',
      'business_address_country',
    ];

    requiredFields.forEach(field => {
      if (!values[field]) {
        errors[field] = 'Required';
      }
    });

    return errors;
  });

  return (
    <MainForm<FieldValues>
      initialValues={mapInitialFormValues(registration)}
      validate={validate}
      onSubmit={onSubmit}
    >
      {formProps => (
        <CenteredContent
          id="business-address"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 100 }}
          exit={{ opacity: 0, y: -20 }}
        >
          <BackButton id="business-address-back" />
          <h2>Operating address</h2>
          <div>
            <p>
              This is the address you work from everyday. It can be your home
              if you run a home-based business.
            </p>
            <AddressField>
              <AddressAutocomplete
                onSelect={(data, geoCode) => {
                  onSelectAddress(geoCode);
                  handleAddressAutocompleteSelect(
                    data,
                    {
                      streetAddress: 'business_address_1',
                      city: 'business_address_city',
                      state: 'business_address_state',
                      zip: 'business_address_zip',
                      country: 'business_address_country',
                    },
                    formProps.form.mutators.setFieldValue,
                  );
                }}
                inputProps={{
                  id: 'business-address-1',
                  label: 'Search Address',
                  value: formProps?.values?.business_address_1 ?? '',
                  icon: ['fas', 'search'],
                }}
              />
            </AddressField>

            <Map
              lat={latLng.lat}
              lng={latLng.lng}
              zoom={zoom}
              onClick={async coords => {
                setLatLng(coords);
                const [geoCode] = await getGeocode({ location: coords });
                const data = processAddressComponents(
                  geoCode.address_components,
                );
                handleAddressAutocompleteSelect(
                  data,
                  {
                    streetAddress: 'business_address_1',
                    city: 'business_address_city',
                    state: 'business_address_state',
                    zip: 'business_address_zip',
                    country: 'business_address_country',
                  },
                  formProps.form.mutators.setFieldValue,
                );
              }}
            />
          </div>
          <NextButton id="business-address-continue" formProps={formProps} />
        </CenteredContent>
      )}
    </MainForm>
  );
};

function mapInitialFormValues(
  registration: Registration | null,
): Partial<FieldValues> {
  return registration
    ? {
        business_address_1: registration.business_address_1,
        business_address_city: registration.business_address_city,
        business_address_state: registration.business_address_state,
        business_address_zip: registration.business_address_zip,
        business_address_country: registration.business_address_country,
      }
    : ({} as any);
}
